import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  Divider,
  IconButton,
  Typography,
  Collapse,
  Box,
  Tooltip,
} from "@mui/material";
import {
  FolderOpen,
  FileText,
  GraduationCap,
  Boxes,
  Shapes,
  SquareTerminal,
  SquareChevronRight,
  Users,
  ChartColumnIncreasing,
  CircleDollarSign,
  Menu as MenuIcon,
  MessageSquareText,
  History,
  Search,
  ChevronRight,
  ChevronDown,
  CircleCheck,
  CircleX,
  EllipsisVertical,
  Archive,
  Pencil,
  ChevronUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import localization from "services/localization";
import { useUserStore } from "context/useUserStore";
import useLanguage from "services/useLanguage";
import SpecificAPI from "services/SpecificApi";
import { ClipLoader } from "react-spinners";
import HistoryActionsMenu from "./HistoryActionsMenu";
import { useLanguageStore } from "context/useLanguageStore";

import { Tooltip as TooltipV2, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { isOutsideDateRange } from "services/utils";

const defaultDeptLabel = localization.departments_management || "Department Management";
const SideMenu = ({
  toggleSidebar,
  collapsed,
  setCollapsed,
  loadHistory,
  selectedHistory,
  setHistories,
  historyList,
  moduleId,
  departmentId,
  loading,
  now,
  setNow,
}) => {
  const { currentLang } = useLanguageStore();
  const [archiveExpanded, setArchiveExpanded] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(defaultDeptLabel);
  const navigate = useNavigate();
  const { appUser, userData, isAppUserLoading, setUserAppData } = useUserStore();
  const [searchQuery, setSearchQuery] = useState("");

  const [visibleCounts, setVisibleCounts] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [editedTitle, setEditedTitle] = useState("");
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedHistoryMemberMessageCount, setSelectedHistoryMemberMessageCount] = useState(0);
  const [selectedHistoryOwnerMessageCount, setSelectedHistoryOwnerMessageCount] = useState(0);

  useEffect(() => {
    if (!userData?.newHistoryTitle) return;

    const { id, title } = userData.newHistoryTitle;
    if (!id || !title) return;

    let currentTitle = "";
    let index = 0;

    const interval = setInterval(() => {
      if (index >= title.length) {
        clearInterval(interval);

        setUserAppData({
          ...userData,
          newHistoryTitle: null,
        });

        return;
      }

      currentTitle += title[index];
      index++;

      setHistories((prev) =>
        prev.map((h) =>
          h.id === id
            ? {
                ...h,
                title: currentTitle,
              }
            : h,
        ),
      );
    }, 10);

    return () => clearInterval(interval);
  }, [userData?.newHistoryTitle]);

  useEffect(() => {
    setSelectedHistoryMemberMessageCount(userData?.selectedHistoryMemberMessageCount || 0);
    setSelectedHistoryOwnerMessageCount(userData?.selectedHistoryOwnerMessageCount || 0);
  }, [userData?.selectedHistoryMemberMessageCount, userData?.selectedHistoryOwnerMessageCount]);

  // 🧭 Menu configuration per role
  const menuConfig = {
    teacher: [
      {
        key: "resources",
        icon: <FolderOpen />,
        label: localization.resources || "Resources",
        action: () => {
          if (moduleId && departmentId) {
            navigate(`/edu-prompt/resources/${moduleId}/${departmentId}`);
          } else {
            navigate("/edu-prompt/resources");
          }
        },
      },
    ],
    administrative: [
      {
        key: "resources",
        icon: <FolderOpen />,
        label: localization.resources || "Resources",
        action: () => {
          if (moduleId && departmentId) {
            navigate(`/edu-prompt/resources/${moduleId}/${departmentId}`);
          } else {
            navigate("/edu-prompt/resources");
          }
        },
      },
      {
        key: "documents",
        icon: <FileText />,
        label: localization.document_history || "Document History",
        action: () => {
          if (moduleId && departmentId) {
            navigate(`/edu-prompt/documents/${moduleId}/${departmentId}`);
          } else {
            navigate("/edu-prompt/documents");
          }
        },
      },
    ],
    root: [
      {
        key: "departments",
        icon: <GraduationCap />,
        label: localization.departments_management || "Department Management",
        action: () => navigate("/edu-prompt/department-management"),
      },
      {
        key: "modules",
        icon: <Boxes />,
        label: localization.modules_management || "Module Management",
        action: () => navigate("/edu-prompt/module-managment"),
      },
      {
        key: "categories",
        icon: <Shapes />,
        label: localization.prompt_categories_management || "Prompt Category Management",
        action: () => navigate("/edu-prompt/prompt-category-managment"),
      },
      {
        key: "prompts",
        icon: <SquareChevronRight />,
        label: localization.prompts_management || "Prompt Management",
        action: () => navigate("/edu-prompt/prompt-managment"),
      },
      {
        key: "templates",
        icon: <SquareTerminal />,
        label: localization.templates_management || "Prompt Template Management",
        action: () => navigate("/edu-prompt/prompt-template-managment"),
      },
      {
        key: "staffs",
        icon: <Users />,
        label: localization.staff_management || "Staff Management",
        action: () => navigate("/edu-prompt/staff-management"),
      },
      {
        key: "resources",
        icon: <FolderOpen />,
        label: localization.resources || "Resources",
        action: () => navigate("/edu-prompt/resources"),
      },
      {
        key: "credits",
        icon: <CircleDollarSign />,
        label: localization.credit_management || "Credit Management",
        action: () => navigate("/edu-prompt/credit-management"),
      },
      {
        key: "analytics",
        icon: <ChartColumnIncreasing />,
        label: localization.anlytics || "Analytics",
        action: () => navigate("/edu-prompt/analytics"),
      },
    ],
  };

  const sortedHistoryList = [...historyList].sort((a, b) => {
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const filteredHistoryList = sortedHistoryList.filter((item) => {
    const query = searchQuery.toLowerCase();
    return item.title?.toLowerCase().includes(query) || item.description?.toLowerCase().includes(query);
  });

  const activeHistories = filteredHistoryList.filter((item) => !item.archived);

  const archivedHistories = filteredHistoryList.filter((item) => item.archived);

  const groupedHistory = activeHistories.reduce((acc, item) => {
    const type = item?.prompt?.id || "Others";

    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {});

  const archivedVisible = visibleCounts["archived"] || 5;
  const displayedArchived = archivedHistories.slice(0, archivedVisible);

  const promptLabels = filteredHistoryList.reduce((acc, item) => {
    const id = item?.prompt?.id || "Others";
    const title = item?.prompt?.title || "Untitled";

    acc[id] = title;

    return acc;
  }, {});

  useEffect(() => {
    if (selectedHistory) {
      // Expand the prompt that contains the selected history
      const selectedItem = historyList.find((item) => item.id === selectedHistory && !item.archived);

      if (!selectedItem) return;

      const promptId = selectedItem?.prompt?.id || "Others";

      setExpandedPrompts((prev) => {
        if (prev[promptId]) return prev;

        return {
          ...prev,
          [promptId]: true,
        };
      });
    } else {
      // No selectedHistory: expand the first prompt by default
      const firstPromptId = activeHistories[0]?.prompt?.id || Object.keys(groupedHistory)[0];

      if (firstPromptId) {
        setExpandedPrompts((prev) => ({
          ...prev,
          [firstPromptId]: true,
        }));
      }
    }
  }, [selectedHistory, historyList]);

  // const isOutsideDateRange = (history) => {
  //   if (
  //     !history?.prompt_data?.datetime_start_playground ||
  //     !history?.prompt_data?.datetime_end_playground
  //   )
  //     return false;

  //   const now = new Date();
  //   const start = new Date(history?.prompt_data?.datetime_start_playground);
  //   const end = new Date(history?.prompt_data?.datetime_end_playground);
  //   return now < start || now > end;
  // };

  const isInTheFuture = (history) => {
    if (!history?.prompt_data?.datetime_start_playground) return false;

    const start = new Date(history?.prompt_data?.datetime_start_playground);

    return now < start;
  };

  const isInTheFutureOrActive = (history) => {
    if (!history?.prompt_data?.datetime_start_playground || !history?.prompt_data?.datetime_end_playground) {
      return false;
    }

    const start = new Date(history?.prompt_data?.datetime_start_playground);
    const end = new Date(history?.prompt_data?.datetime_end_playground);

    return now < start || now < end;
  };

  const updateHistoryData = (action, history, data) => {
    const updatedHistoryData = { ...(history.prompt_data || {}) };

    if (action === "activate") {
      if (
        updatedHistoryData.datetime_end_playground &&
        new Date(updatedHistoryData.datetime_end_playground) < new Date()
      ) {
        toast.error(
          localization.cannot_activate_history_past_end_date || "Cannot activate history. The end date is in the past.",
        );
        return;
      }
      updatedHistoryData.datetime_start_playground = new Date().toISOString(); //.split(".")[0];
      SpecificAPI.updateHistory(history.id, { prompt_data: updatedHistoryData }, userData?.selected_school_id)
        .then((res) => {
          toast.success(localization.history_activated_successfully || "History activated successfully!");
          setHistories((prev) => prev.map((h) => (h.id === res.data.id ? res.data : h)));
          setNow(new Date());
          setUserAppData({
            ...userData,
            updatedHistory: {
              id: history.id,
              datetime_start_playground: updatedHistoryData.datetime_start_playground,
              datetime_end_playground: updatedHistoryData.datetime_end_playground,
            },
          });
        })
        .catch(() => {
          toast.error(localization.failed_to_activate_history || "Failed to activate history.");
        });
    } else if (action === "deactivate") {
      // updatedHistoryData.datetime_end_playground = new Date().toISOString();
      updatedHistoryData.datetime_end_playground = new Date().toISOString(); //.split(".")[0]
      SpecificAPI.updateHistory(history.id, { prompt_data: updatedHistoryData }, userData?.selected_school_id)
        .then((res) => {
          toast.success(localization.history_deactivated_successfully || "History deactivated successfully!");
          setHistories((prev) => prev.map((h) => (h.id === res.data.id ? res.data : h)));
          setNow(new Date());
          setUserAppData({
            ...userData,
            updatedHistory: {
              id: history.id,
              datetime_start_playground: updatedHistoryData.datetime_start_playground,
              datetime_end_playground: updatedHistoryData.datetime_end_playground,
            },
          });
        })
        .catch(() => {
          toast.error(localization.failed_to_deactivate_history || "Failed to deactivate history.");
        });
    } else if (action === "edit") {
      SpecificAPI.updateHistory(history, { title: data }, userData?.selected_school_id)
        .then((res) => {
          toast.success(localization.history_updated_successfully || "History updated successfully!");
          setEditingHistoryId(null);
          setHistories((prev) => prev.map((h) => (h.id === res.data.id ? res.data : h)));
        })
        .catch(() => {
          toast.error(localization.failed_to_update_history || "Failed to update history.");
          setEditingHistoryId(null);
        });
    } else if (action === "archive") {
      SpecificAPI.updateHistory(history.id, { archived: true }, userData?.selected_school_id)
        .then((res) => {
          toast.success(localization.history_archived_successfully || "History archived successfully!");
          setHistories((prev) => prev.map((h) => (h.id === res.data.id ? res.data : h)));
        })
        .catch(() => {
          toast.error(localization.failed_to_archive_history || "Failed to archive history.");
        });
    } else if (action === "unarchive") {
      SpecificAPI.updateHistory(history.id, { archived: false }, userData?.selected_school_id)
        .then((res) => {
          toast.success(
            localization.history_removed_from_archive_successfully || "History removed from archive successfully!",
          );
          setHistories((prev) => prev.map((h) => (h.id === res.data.id ? res.data : h)));
        })
        .catch(() => {
          toast.error(localization.failed_to_remove_history_from_archive || "Failed to remove history from archive.");
        });
    }
  };

  const togglePrompt = (type) => {
    setExpandedPrompts((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (isAppUserLoading || !appUser) {
    return <div className="text-muted-foreground">{localization.loading || "Loading..."}</div>;
  }

  return (
    <>
      <Drawer
        variant="permanent"
        className={`${collapsed ? "mini-sidebar collapsed" : "sidebar expanded"}`}
        classes={{
          paper: collapsed ? "sidebar-collapsed" : "sidebar-expanded",
        }}
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: "var(--sidebar-bg)",
            color: "var(--sidebar-foreground)",
            borderRight: "1px solid var(--sidebar-border)",
            display: "flex",
            flexDirection: "column",
            height: "100vh",
          },
        }}
      >
        <div style={{ flexShrink: 0 }}>
          {/* ===== Header ===== */}

          <div className="sidebar-header">
            {!collapsed && (
              <>
                <div className="main-icon flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="text-base font-semibold page-title text-foreground whitespace-nowrap animate-fade-in">
                  {localization.EDU_PROMPT || "EDU PROMPT"}
                </span>
              </>
            )}
            <IconButton onClick={toggleSidebar} className="menu-button">
              <MenuIcon className="w-5 h-5" />
            </IconButton>
          </div>

          <Divider sx={{ borderColor: "var(--sidebar-border)" }} />

          {/* ===== Role-Based Menu ===== */}
          <List>
            {(menuConfig[appUser.role] || []).map((item, i) => {
              const isSelected = selectedMenu === item.key || item.highlight;
              const IconWithColor = React.cloneElement(item.icon, {
                style: {
                  color: isSelected ? "var(--sidebar-accent-foreground)" : "",
                },
              });
              return (
                <ListItem
                  key={i}
                  button
                  onClick={() => {
                    item.action();
                    setSelectedMenu(item.key);
                  }}
                  style={{ cursor: "pointer" }}
                  sx={{
                    borderRadius: "8px",
                    mx: 0,
                    mb: 0.5,
                    px: 2,
                    transition: "all 0.2s ease",

                    color: selectedMenu === item.key ? "var(--sidebar-accent-foreground)" : "var(--sidebar-foreground)",

                    backgroundColor: selectedMenu === item.key ? "var(--sidebar-accent)" : "transparent",

                    "&:hover": {
                      backgroundColor: selectedMenu === item.key ? "var(--sidebar-accent)" : "var(--sidebar-muted)",
                    },

                    "& .MuiListItemIcon-root": {
                      color:
                        selectedMenu === item.key
                          ? "var(--sidebar-accent-foreground)"
                          : "var(--sidebar-muted-foreground)",
                      minWidth: 36,
                    },
                  }}
                >
                  <ListItemIcon>{IconWithColor}</ListItemIcon>
                  {!collapsed && (
                    <span
                      className={`text-sm font-medium ${isSelected ? "text-[var(--sidebar-accent-foreground)]" : "text-[var(--sidebar-foreground)]"}`}
                    >
                      {item.label}
                    </span>
                  )}
                </ListItem>
              );
            })}
          </List>
        </div>
        {/* ===== Teacher-only sections ===== */}
        {(appUser?.role === "teacher" || appUser?.role == "administrative" || appUser?.role == "student") && (
          <>
            <div style={{ flexShrink: 0 }}>
              <Divider sx={{ borderColor: "var(--sidebar-border)" }} />

              {/* --- History Search --- */}
              {!collapsed && (
                <div className="px-3 py-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-sidebar-muted-foreground" />
                    <input
                      type="text"
                      placeholder={localization.search_history || "Search history..."}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-sidebar-border bg-sidebar-muted text-foreground placeholder:text-sidebar-muted-foreground focus:outline-none focus:ring-1 focus:ring-sidebar-ring sidebar-transition"
                    />
                  </div>
                </div>
              )}
              {/* --- History --- */}
              <ListItem
                button={false}
                sx={{
                  cursor: "default",
                  pl: 2,
                  opacity: 0.6,
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
                style={{ cursor: "pointer" }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: "var(--sidebar-muted-foreground)",
                  }}
                >
                  <History className="w-5 h-5" />
                </ListItemIcon>
                {!collapsed && (
                  <span
                    className="text-sm font-medium text-[var(--sidebar-foreground)]"
                    style={{ textTransform: "uppercase" }}
                  >
                    {localization.history || "History"}
                  </span>
                )}
              </ListItem>
            </div>

            {!collapsed && (
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                }}
              >
                {loading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      paddingTop: "2rem",
                    }}
                  >
                    <ClipLoader size={30} color="var(--primary-icon)" loading={true} />
                  </div>
                ) : (
                  <>
                    {Object.entries(groupedHistory).map(([type, histories]) => {
                      const visible = visibleCounts[type] || 5;
                      const displayedHistories = histories.slice(0, visible);
                      const isExpanded = expandedPrompts[type];

                      return (
                        <div key={type}>
                          <ListItem
                            button
                            onClick={() => togglePrompt(type)}
                            sx={{
                              borderRadius: "8px",
                              pl: "1.6rem",
                              mx: 0,
                              mb: 0.5,
                              "&:hover": {
                                backgroundColor: "var(--sidebar-muted)",
                              },
                            }}
                          >
                            {!collapsed && (
                              <span
                                className="text-sm font-semibold text-[var(--sidebar-foreground)]"
                                style={{
                                  textTransform: "capitalize",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                {/* Chevron as part of text */}
                                <span style={{ fontSize: "0.8rem" }}>
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </span>
                                {promptLabels[type]?.[currentLang] ||
                                  promptLabels[type]?.["It"] ||
                                  promptLabels[type]?.["En"] ||
                                  type}
                              </span>
                            )}
                          </ListItem>

                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {displayedHistories.map((msg) => {
                              const isDisabled = isOutsideDateRange(msg, now);
                              const currentCost = selectedHistoryMemberMessageCount * msg?.prompt?.prompt_cost || 0;
                              const extraCost = selectedHistoryOwnerMessageCount * msg?.prompt?.prompt_cost || 0;
                              const historyCredit = {
                                forecastedCost: msg.forecasted_cost,
                                currentCost: currentCost,
                                finalCost: msg.member_cost + msg.owner_cost || "N/A",
                                availableCredit: msg.forecasted_cost - currentCost,
                                status: msg.status,
                                forecastedCostPercentage:
                                  ((msg.forecasted_cost - currentCost) / msg.forecasted_cost) * 100,
                                currentCostPercentage: (currentCost / msg.forecasted_cost) * 100,
                              };

                              return (
                                <ListItem
                                  key={msg.id}
                                  sx={{
                                    cursor: isDisabled ? "default" : "pointer",
                                    pl: 4,
                                    // opacity: isDisabled ? 0.5 : 1,
                                    borderRadius: "8px",
                                    backgroundColor:
                                      selectedHistory === msg.id ? "var(--sidebar-accent)" : "transparent",
                                    "&:hover": {
                                      backgroundColor:
                                        selectedHistory === msg.id ? "var(--sidebar-accent)" : "var(--sidebar-muted)",
                                    },
                                  }}
                                  button={!isDisabled}
                                  onClick={() => {
                                    // if (!isDisabled)
                                    loadHistory(msg.id, msg.classroomId);
                                  }}
                                >
                                  {/* Message Icon */}
                                  {editingHistoryId !== msg.id && (
                                    <MessageSquareText
                                      className={isDisabled ? "opacity-50 w-4 h-4" : "w-4 h-4"}
                                      style={{
                                        color: "var(--sidebar-muted-foreground)",
                                        marginRight: "0.5rem",
                                      }}
                                    />
                                  )}

                                  {/* Title & Status */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      flex: 1,
                                    }}
                                  >
                                    {/* Title / Edit Field */}
                                    {editingHistoryId === msg.id ? (
                                      <span
                                        className={
                                          isDisabled ? "opacity-50 flex items-center gap-1" : "flex items-center gap-1"
                                        }
                                      >
                                        <input
                                          value={editedTitle}
                                          onChange={(e) => setEditedTitle(e.target.value)}
                                          onClick={(e) => e.stopPropagation()}
                                          autoFocus
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") updateHistoryData("edit", msg.id, editedTitle);
                                          }}
                                          className="text-sm border-b border-gray-300 focus:outline-none focus:border-primary"
                                        />
                                        {/* ✅ Save Button */}
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            updateHistoryData("edit", msg.id, editedTitle);
                                          }}
                                          className="w-6 h-6 flex items-center justify-center rounded hover:bg-green-100 focus:outline-none"
                                          title="Save"
                                        >
                                          <CircleCheck color="#16a34a" className="w-4 h-4" />
                                        </button>

                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingHistoryId(null);
                                          }}
                                          className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 focus:outline-none"
                                          title="Cancel"
                                        >
                                          <CircleX color="#ef4444" className="w-4 h-4" />
                                        </button>
                                      </span>
                                    ) : (
                                      <span
                                        className={`text-sm ${
                                          selectedHistory === msg.id
                                            ? "text-[var(--sidebar-accent-foreground)]"
                                            : "text-[var(--sidebar-foreground)]"
                                        } ${isDisabled ? "opacity-50" : ""}`}
                                      >
                                        {msg.title}
                                      </span>
                                    )}

                                    {/* Date & Status Dot + Timestamp */}
                                    <div
                                      className={
                                        isDisabled
                                          ? "opacity-50 flex items-center gap-1 mt-1 text-[0.7rem]"
                                          : "flex items-center gap-1 mt-1 text-[0.7rem]"
                                      }
                                    >
                                      {/* Status Dot */}
                                      {msg?.prompt_data?.datetime_end_playground && (
                                        <Tooltip
                                          title={
                                            isInTheFuture(msg)
                                              ? "Upcoming"
                                              : !isOutsideDateRange(msg, now)
                                                ? "Active"
                                                : "Inactive"
                                          }
                                          placement="top"
                                        >
                                          <span
                                            className={`inline-block w-1.5 h-1.5 rounded-full ${isInTheFuture(msg) ? "bg-blue-500" : !isOutsideDateRange(msg, now) ? "bg-green-500" : "bg-red-500"}`}
                                          />
                                        </Tooltip>
                                      )}

                                      {/* Date / Timestamp */}
                                      {msg?.prompt_data?.datetime_end_playground && (
                                        <span
                                          className={`text-[0.7rem] ${selectedHistory === msg.id ? "text-[var(--sidebar-accent-foreground)]" : "text-gray-500"} ${isDisabled ? "opacity-50" : ""}`}
                                        >
                                          {new Date(msg?.prompt_data?.datetime_end_playground).toLocaleString()}
                                        </span>
                                      )}
                                    </div>

                                    {/* cost */}
                                    {(appUser?.role === "teacher" || appUser?.role === "administrative") &&
                                      selectedHistory === msg.id && (
                                        <div className="flex flex-col items-end gap-1">
                                          <TooltipProvider>
                                            <TooltipV2>
                                              <TooltipTrigger asChild>
                                                <div className="relative h-1 w-full overflow-hidden rounded-full bg-muted flex">
                                                  {/* Used */}
                                                  <div
                                                    className="h-full bg-[var(--primary-icon)]"
                                                    style={{
                                                      width: `${historyCredit.currentCostPercentage}%`,
                                                    }}
                                                  />

                                                  {/* Frozen */}
                                                  <div
                                                    className="h-full bg-blue-400"
                                                    style={{
                                                      width: `${historyCredit.forecastedCostPercentage}%`,
                                                    }}
                                                  />
                                                </div>
                                              </TooltipTrigger>

                                              <TooltipContent className="bg-[var(--sidebar-bg)]">
                                                <div className="text-xs space-y-1 w-40">
                                                  <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                      <span className="w-2 h-2 rounded-full bg-[var(--primary-icon)]" />
                                                      <span>Current Cost</span>
                                                    </div>
                                                    <span className="font-medium">{historyCredit.currentCost}</span>
                                                  </div>

                                                  <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                      <span className="w-2 h-2 rounded-full bg-blue-400" />
                                                      <span>Forecasted Cost</span>
                                                    </div>
                                                    <span className="font-medium">{historyCredit.forecastedCost}</span>
                                                  </div>

                                                  <div className="flex items-center justify-between border-t pt-1 mt-1">
                                                    <span className="text-muted-foreground">
                                                      {historyCredit.status == "expired"
                                                        ? "Final Cost"
                                                        : "Available Credit"}
                                                    </span>
                                                    <span className="font-semibold">
                                                      {historyCredit.status == "expired"
                                                        ? historyCredit.finalCost
                                                        : historyCredit.availableCredit}
                                                    </span>
                                                  </div>
                                                </div>
                                              </TooltipContent>
                                            </TooltipV2>
                                          </TooltipProvider>
                                        </div>
                                      )}
                                  </Box>

                                  {/* More Menu (Teacher Only) */}
                                  {(appUser?.role === "teacher" || appUser?.role === "administrative") &&
                                    editingHistoryId !== msg.id && (
                                      <HistoryActionsMenu
                                        msg={msg}
                                        openMenuId={openMenuId}
                                        setOpenMenuId={setOpenMenuId}
                                        setSelectedHistoryItem={setSelectedHistoryItem}
                                        setEditingHistoryId={setEditingHistoryId}
                                        setEditedTitle={setEditedTitle}
                                        updateHistoryData={updateHistoryData}
                                        localization={localization}
                                        isOutsideDateRange={isOutsideDateRange}
                                        isInTheFutureOrActive={isInTheFutureOrActive}
                                        now={now}
                                      />
                                    )}
                                </ListItem>
                              );
                            })}

                            {/* 🔹 KEEP SHOW MORE EXACTLY SAME */}
                            {visible < histories.length && (
                              <ListItem
                                sx={{
                                  pl: 6,
                                  "&:hover": { backgroundColor: "transparent" },
                                }}
                                button
                                onClick={() =>
                                  setVisibleCounts((prev) => ({
                                    ...prev,
                                    [type]: (prev[type] || 5) + 10,
                                  }))
                                }
                              >
                                <Typography variant="caption" color="primary" sx={{ ml: "auto", cursor: "pointer" }}>
                                  {localization.show_more || "Show more"}
                                </Typography>
                              </ListItem>
                            )}
                          </Collapse>
                        </div>
                      );
                    })}

                    {/* Archived Histories */}
                    {archivedHistories.length > 0 && (
                      <div className="pt-3">
                        <ListItem
                          button={false}
                          onClick={() => {
                            setArchiveExpanded(!archiveExpanded);
                            setCollapsed(false);
                          }}
                          sx={{
                            cursor: "default",
                            pl: 2,
                            opacity: 0.6,
                            "&:hover": { backgroundColor: "transparent" },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 32,
                              color: "var(--sidebar-muted-foreground)",
                            }}
                          >
                            <Archive className="w-5 h-5" />
                          </ListItemIcon>
                          {!collapsed && (
                            <span
                              className="text-sm font-medium text-[var(--sidebar-foreground)]"
                              style={{ textTransform: "uppercase" }}
                            >
                              {localization.archived || "Archived"}
                            </span>
                          )}
                          <div className="ml-auto">
                            {!collapsed &&
                              (archiveExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              ))}
                          </div>
                        </ListItem>
                        <Collapse in={archiveExpanded} timeout="auto" unmountOnExit>
                          {displayedArchived.map((msg) => (
                            <ListItem
                              key={msg.id}
                              sx={{
                                pl: 4,
                                opacity: 0.6,
                                "&:hover": {
                                  backgroundColor:
                                    selectedHistory === msg.id ? "var(--sidebar-accent)" : "var(--sidebar-muted)",
                                },
                                borderRadius: "8px",
                                backgroundColor: selectedHistory === msg.id ? "var(--sidebar-accent)" : "transparent",
                              }}
                              button
                              onClick={() => loadHistory(msg.id, msg.classroomId)}
                            >
                              <MessageSquareText
                                className="w-4 h-4"
                                style={{
                                  color: "var(--sidebar-muted-foreground)",
                                  marginRight: "0.5rem",
                                }}
                              />

                              <span className={`text-sm ${"text-[var(--sidebar-foreground)]"}`}>{msg.title}</span>

                              {(appUser?.role === "teacher" || appUser?.role === "administrative") &&
                                editingHistoryId != msg.id && (
                                  <HistoryActionsMenu
                                    msg={msg}
                                    openMenuId={openMenuId}
                                    setOpenMenuId={setOpenMenuId}
                                    setSelectedHistoryItem={setSelectedHistoryItem}
                                    setEditingHistoryId={setEditingHistoryId}
                                    setEditedTitle={setEditedTitle}
                                    updateHistoryData={updateHistoryData}
                                    localization={localization}
                                    isOutsideDateRange={isOutsideDateRange}
                                    isInTheFutureOrActive={isInTheFutureOrActive}
                                  />
                                )}
                            </ListItem>
                          ))}
                        </Collapse>
                        {/* Show More */}
                        {archivedVisible < archivedHistories.length && (
                          <ListItem
                            sx={{
                              pl: 6,
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                            }}
                            button
                            onClick={() =>
                              setVisibleCounts((prev) => ({
                                ...prev,
                                archived: (prev.archived || 5) + 10,
                              }))
                            }
                          >
                            <Typography
                              variant="caption"
                              color="primary"
                              sx={{
                                ml: "auto",
                                cursor: "pointer",
                              }}
                            >
                              {localization.show_more || "Show more"}
                            </Typography>
                          </ListItem>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
};

export default SideMenu;
